import React, { useMemo } from "react";

import { useGetCarbonProgramByFarmId, useNavigate, useTranslation, useUserContext } from "utils";

import { Flex, LazyLoadImage, Skeleton, Typography } from "components";

enum ONBOARDING_BANNER_TYPE {
  FOOTPRINTING_DAIRY_ONLY = "footprintingDairyOnly",
  NON_ENROLLED_WITHOUT_FIELDS = "nonEnrolledWithoutFields",
  NON_ENROLLED_WITH_FIELDS = "nonEnrolledWithFields",
  ENROLLED_WITHOUT_FIELDS = "enrolledWithoutFields",
  ENROLLED_WITH_FIELDS = "enrolledWithFields",
  ENROLLED_WITH_FIELDS_AND_FOOTPRINTING = "enrolledWithFieldsAndFootprinting",
}

const OnboardingBanner: React.FC = () => {
  const { t } = useTranslation("dashboard");
  const navigate = useNavigate();
  const { user } = useUserContext();

  const { data: carbonProgram, isLoading: isLoadingCarbonProgram } = useGetCarbonProgramByFarmId(user?.farm.id ?? undefined);

  const farmHasFields = !!user?.farm?.fields && user?.farm?.fields > 0;
  const showAddFieldsButton = carbonProgram?.platformFeatures?.fieldManagement?.enabled ?? true;
  const farmHasFootprintingEnabled = carbonProgram?.platformFeatures?.footprinting?.enabled && carbonProgram?.farmLevelFootprinting?.startYear;

  const bannerType = useMemo(() => {
    if (farmHasFootprintingEnabled && carbonProgram?.platformFeatures?.footprinting?.dairy && carbonProgram?.farmLevelFootprinting?.programCrops.length === 0) {
      return ONBOARDING_BANNER_TYPE.FOOTPRINTING_DAIRY_ONLY;
    }

    const farmIsEnrolled = !!carbonProgram;
    if (farmIsEnrolled && farmHasFields && farmHasFootprintingEnabled) {
      return ONBOARDING_BANNER_TYPE.ENROLLED_WITH_FIELDS_AND_FOOTPRINTING;
    }

    if (farmIsEnrolled) {
      return farmHasFields ? ONBOARDING_BANNER_TYPE.ENROLLED_WITH_FIELDS : ONBOARDING_BANNER_TYPE.ENROLLED_WITHOUT_FIELDS;
    }

    if (!farmIsEnrolled) {
      return farmHasFields ? ONBOARDING_BANNER_TYPE.NON_ENROLLED_WITH_FIELDS : ONBOARDING_BANNER_TYPE.NON_ENROLLED_WITHOUT_FIELDS;
    }

    return ONBOARDING_BANNER_TYPE.NON_ENROLLED_WITHOUT_FIELDS;
  }, [carbonProgram, farmHasFields, farmHasFootprintingEnabled]);

  return !user || isLoadingCarbonProgram ? (
    <Skeleton className="onboarding-banner-container-skeleton" type="block" height="20rem" />
  ) : (
    <Flex justify="space-between" className="onboarding-banner-container" gap="2rem">
      <div className="onboarding-banner-image">
        {carbonProgram?.bannerImageUrl && <LazyLoadImage src={carbonProgram.bannerImageUrl} alt="carbon-program-banner" />}
      </div>
      <Flex vertical justify="space-between" className="onboarding-banner-text" gap="1.5rem">
        <Typography variant="h1" hero className="margin-bottom-150" color="white">
          {t(`onboardingBanner.${bannerType}.title`)}
        </Typography>
        <Typography variant="paragraph-xl" color="white">
          {t(`onboardingBanner.${bannerType}.body`)}
        </Typography>
      </Flex>
      {(showAddFieldsButton || farmHasFootprintingEnabled) && (
        <Flex vertical justify="flex-end" className="onboarding-banner-button-container" gap="1rem">
          {showAddFieldsButton ? (
            <Typography
              variant="paragraph-m"
              role="button"
              tabIndex={0}
              aria-label={t(`onboardingBanner.${bannerType}.CTAs.primary`)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate("/all-fields");
                }
              }}
              onClick={() => navigate("/all-fields")}
              className="onboarding-button-primary"
            >
              {t(`onboardingBanner.${bannerType}.CTAs.primary`)}
            </Typography>
          ) : null}
          {farmHasFootprintingEnabled ? (
            <Typography
              variant="paragraph-m"
              role="button"
              tabIndex={0}
              aria-label={t("onboardingBanner.enrolledWithFieldsAndFootprinting.CTAs.secondary")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate("/footprinting");
                }
              }}
              onClick={() => navigate("/footprinting")}
              className="onboarding-button-secondary"
            >
              {t("onboardingBanner.enrolledWithFieldsAndFootprinting.CTAs.secondary")}
            </Typography>
          ) : null}
        </Flex>
      )}
    </Flex>
  );
};

export default OnboardingBanner;
